<template>
  <div>
    <h2 class="mb-4">HaioFlash Admin Dashboard</h2>
    
    <!-- Statistics Cards -->
    <vs-row>
      <vs-col vs-w="3" vs-sm="6" vs-xs="12" class="mb-4">
        <vx-card class="text-center">
          <div class="p-4">
            <feather-icon icon="ServerIcon" svgClasses="h-12 w-12 text-primary mx-auto mb-2"></feather-icon>
            <h3 class="text-3xl font-bold">{{ stats.total_vms || 0 }}</h3>
            <p class="text-grey">Total VMs</p>
          </div>
        </vx-card>
      </vs-col>
      
      <vs-col vs-w="3" vs-sm="6" vs-xs="12" class="mb-4">
        <vx-card class="text-center">
          <div class="p-4">
            <feather-icon icon="ActivityIcon" svgClasses="h-12 w-12 text-success mx-auto mb-2"></feather-icon>
            <h3 class="text-3xl font-bold">{{ stats.running_vms || 0 }}</h3>
            <p class="text-grey">Running VMs</p>
          </div>
        </vx-card>
      </vs-col>
      
      <vs-col vs-w="3" vs-sm="6" vs-xs="12" class="mb-4">
        <vx-card class="text-center">
          <div class="p-4">
            <feather-icon icon="UsersIcon" svgClasses="h-12 w-12 text-warning mx-auto mb-2"></feather-icon>
            <h3 class="text-3xl font-bold">{{ stats.total_customers || 0 }}</h3>
            <p class="text-grey">Customers</p>
          </div>
        </vx-card>
      </vs-col>
      
      <vs-col vs-w="3" vs-sm="6" vs-xs="12" class="mb-4">
        <vx-card class="text-center">
          <div class="p-4">
            <feather-icon icon="DollarSignIcon" svgClasses="h-12 w-12 text-success mx-auto mb-2"></feather-icon>
            <h3 class="text-3xl font-bold">{{ formatCurrency(revenue.monthly_revenue || 0) }}</h3>
            <p class="text-grey">This Month Revenue</p>
          </div>
        </vx-card>
      </vs-col>
    </vs-row>
    
    <!-- Revenue Breakdown -->
    <vs-row class="mb-4">
      <vs-col vs-w="12">
        <vx-card title="Revenue Overview">
          <vs-row>
            <vs-col vs-w="3" vs-sm="6" vs-xs="12" class="mb-4">
              <div class="text-center p-4">
                <feather-icon icon="DollarSignIcon" svgClasses="h-12 w-12 text-success mx-auto mb-2"></feather-icon>
                <h3 class="text-3xl font-bold">{{ formatCurrency(revenue.total_revenue || 0) }}</h3>
                <p class="text-grey">Total Historical Revenue</p>
                <small class="text-grey">(All time)</small>
              </div>
            </vs-col>
            
            <vs-col vs-w="3" vs-sm="6" vs-xs="12" class="mb-4">
              <div class="text-center p-4">
                <feather-icon icon="ServerIcon" svgClasses="h-12 w-12 text-primary mx-auto mb-2"></feather-icon>
                <h3 class="text-3xl font-bold">{{ formatCurrency(revenue.total_vm_revenue || 0) }}</h3>
                <p class="text-grey">Total VM Revenue</p>
                <small class="text-grey">(Purchases + Renewals)</small>
              </div>
            </vs-col>
            
            <vs-col vs-w="3" vs-sm="6" vs-xs="12" class="mb-4">
              <div class="text-center p-4">
                <feather-icon icon="HashIcon" svgClasses="h-12 w-12 text-warning mx-auto mb-2"></feather-icon>
                <h3 class="text-3xl font-bold">{{ formatCurrency(revenue.total_ipv4_addon_revenue || 0) }}</h3>
                <p class="text-grey">Total IPv4 Revenue</p>
                <small class="text-grey">(Addons + Renewals + Updates)</small>
              </div>
            </vs-col>
            
            <vs-col vs-w="3" vs-sm="6" vs-xs="12" class="mb-4">
              <div class="text-center p-4">
                <feather-icon icon="WifiIcon" svgClasses="h-12 w-12 text-info mx-auto mb-2"></feather-icon>
                <h3 class="text-3xl font-bold">{{ formatCurrency(revenue.total_traffic_revenue || 0) }}</h3>
                <p class="text-grey">Total Traffic Revenue</p>
                <small class="text-grey">(Traffic Packages)</small>
              </div>
            </vs-col>
          </vs-row>
          
          <!-- Daily Stats Row -->
          <vs-divider></vs-divider>
          <vs-row>
            <vs-col vs-w="4" vs-sm="6" vs-xs="12" class="mb-4">
              <div class="text-center p-4">
                <feather-icon icon="CalendarIcon" svgClasses="h-8 w-8 text-success mx-auto mb-2"></feather-icon>
                <h3 class="text-2xl font-bold">{{ formatCurrency(revenue.today_revenue || 0) }}</h3>
                <p class="text-grey">Today's Revenue</p>
              </div>
            </vs-col>
            
            <vs-col vs-w="4" vs-sm="6" vs-xs="12" class="mb-4">
              <div class="text-center p-4">
                <feather-icon icon="TrendingUpIcon" svgClasses="h-8 w-8 text-primary mx-auto mb-2"></feather-icon>
                <h3 class="text-2xl font-bold">{{ formatCurrency(revenue.monthly_revenue || 0) }}</h3>
                <p class="text-grey">This Month's Revenue</p>
              </div>
            </vs-col>
            
            <vs-col vs-w="4" vs-sm="6" vs-xs="12" class="mb-4">
              <div class="text-center p-4">
                <feather-icon icon="RefreshCwIcon" svgClasses="h-8 w-8 text-warning mx-auto mb-2"></feather-icon>
                <h3 class="text-2xl font-bold">{{ formatCurrency((revenue.vm_renewal_revenue || 0) + (revenue.ipv4_addon_renewal_revenue || 0)) }}</h3>
                <p class="text-grey">Total Renewal Revenue</p>
              </div>
            </vs-col>
          </vs-row>
        </vx-card>
      </vs-col>
    </vs-row>
    
    <!-- IP Statistics -->
    <vs-row class="mb-4">
      <vs-col vs-w="12">
        <vx-card title="IP Address Pool Statistics">
          <vs-row>
            <vs-col vs-w="3" vs-sm="6" vs-xs="12" class="mb-4">
              <div class="text-center p-4">
                <feather-icon icon="HashIcon" svgClasses="h-12 w-12 text-primary mx-auto mb-2"></feather-icon>
                <h3 class="text-3xl font-bold">{{ ipStats.total_ips || 0 }}</h3>
                <p class="text-grey">Total IPs in Pool</p>
              </div>
            </vs-col>
            
            <vs-col vs-w="3" vs-sm="6" vs-xs="12" class="mb-4">
              <div class="text-center p-4">
                <feather-icon icon="CheckCircleIcon" svgClasses="h-12 w-12 text-success mx-auto mb-2"></feather-icon>
                <h3 class="text-3xl font-bold">{{ ipStats.allocated_ips || 0 }}</h3>
                <p class="text-grey">Allocated to VMs</p>
              </div>
            </vs-col>
            
            <vs-col vs-w="3" vs-sm="6" vs-xs="12" class="mb-4">
              <div class="text-center p-4">
                <feather-icon icon="CircleIcon" svgClasses="h-12 w-12 text-primary mx-auto mb-2"></feather-icon>
                <h3 class="text-3xl font-bold">{{ ipStats.free_ips || 0 }}</h3>
                <p class="text-grey">Available</p>
              </div>
            </vs-col>
            
            <vs-col vs-w="3" vs-sm="6" vs-xs="12" class="mb-4">
              <div class="text-center p-4">
                <feather-icon icon="LockIcon" svgClasses="h-12 w-12 text-warning mx-auto mb-2"></feather-icon>
                <h3 class="text-3xl font-bold">{{ ipStats.reserved_ips || 0 }}</h3>
                <p class="text-grey">Reserved</p>
              </div>
            </vs-col>
          </vs-row>
        </vx-card>
      </vs-col>
    </vs-row>
    
    <!-- IPv4 Addon Statistics -->
    <vs-row class="mb-4">
      <vs-col vs-w="12">
        <vx-card title="IPv4 Addon Marketplace Statistics">
          <vs-row>
            <vs-col vs-w="3" vs-sm="6" vs-xs="12" class="mb-4">
              <div class="text-center p-4">
                <feather-icon icon="PlusCircleIcon" svgClasses="h-12 w-12 text-primary mx-auto mb-2"></feather-icon>
                <h3 class="text-3xl font-bold">{{ ipStats.total_ipv4_addons || 0 }}</h3>
                <p class="text-grey">Total IPv4 Addons</p>
              </div>
            </vs-col>
            
            <vs-col vs-w="3" vs-sm="6" vs-xs="12" class="mb-4">
              <div class="text-center p-4">
                <feather-icon icon="CheckCircleIcon" svgClasses="h-12 w-12 text-success mx-auto mb-2"></feather-icon>
                <h3 class="text-3xl font-bold">{{ ipStats.active_ipv4_addons || 0 }}</h3>
                <p class="text-grey">Active Addons</p>
              </div>
            </vs-col>
            
            <vs-col vs-w="3" vs-sm="6" vs-xs="12" class="mb-4">
              <div class="text-center p-4">
                <feather-icon icon="ClockIcon" svgClasses="h-12 w-12 text-warning mx-auto mb-2"></feather-icon>
                <h3 class="text-3xl font-bold">{{ ipStats.pending_ipv4_addons || 0 }}</h3>
                <p class="text-grey">Pending</p>
              </div>
            </vs-col>
            
            <vs-col vs-w="3" vs-sm="6" vs-xs="12" class="mb-4">
              <div class="text-center p-4">
                <feather-icon icon="XCircleIcon" svgClasses="h-12 w-12 text-danger mx-auto mb-2"></feather-icon>
                <h3 class="text-3xl font-bold">{{ ipStats.failed_ipv4_addons || 0 }}</h3>
                <p class="text-grey">Failed</p>
              </div>
            </vs-col>
          </vs-row>
        </vx-card>
      </vs-col>
    </vs-row>
    
    <!-- IPv4 Addon Statistics -->
    <vs-row class="mb-4">
      <vs-col vs-w="12">
        <vx-card title="IPv4 Addon Statistics">
          <vs-row>
            <vs-col vs-w="3" vs-sm="6" vs-xs="12" class="mb-4">
              <div class="text-center p-4">
                <feather-icon icon="PlusCircleIcon" svgClasses="h-12 w-12 text-primary mx-auto mb-2"></feather-icon>
                <h3 class="text-3xl font-bold">{{ ipStats.total_ipv4_addons || 0 }}</h3>
                <p class="text-grey">Total IPv4 Addons</p>
              </div>
            </vs-col>
            
            <vs-col vs-w="3" vs-sm="6" vs-xs="12" class="mb-4">
              <div class="text-center p-4">
                <feather-icon icon="CheckCircleIcon" svgClasses="h-12 w-12 text-success mx-auto mb-2"></feather-icon>
                <h3 class="text-3xl font-bold">{{ ipStats.active_ipv4_addons || 0 }}</h3>
                <p class="text-grey">Active Addons</p>
              </div>
            </vs-col>
            
            <vs-col vs-w="3" vs-sm="6" vs-xs="12" class="mb-4">
              <div class="text-center p-4">
                <feather-icon icon="ClockIcon" svgClasses="h-12 w-12 text-warning mx-auto mb-2"></feather-icon>
                <h3 class="text-3xl font-bold">{{ ipStats.pending_ipv4_addons || 0 }}</h3>
                <p class="text-grey">Pending Configuration</p>
              </div>
            </vs-col>
            
            <vs-col vs-w="3" vs-sm="6" vs-xs="12" class="mb-4">
              <div class="text-center p-4">
                <feather-icon icon="XCircleIcon" svgClasses="h-12 w-12 text-danger mx-auto mb-2"></feather-icon>
                <h3 class="text-3xl font-bold">{{ ipStats.failed_ipv4_addons || 0 }}</h3>
                <p class="text-grey">Failed Addons</p>
              </div>
            </vs-col>
          </vs-row>
        </vx-card>
      </vs-col>
    </vs-row>
    
    <!-- Quick Actions -->
    <vs-row class="mb-4">
      <vs-col vs-w="12">
        <vx-card title="Quick Actions">
          <vs-row>
            <vs-col vs-w="3" vs-sm="6" vs-xs="12" class="mb-2">
              <vs-button color="primary" class="w-full" to="/haioflash/plans">
                <feather-icon icon="PackageIcon" class="mr-2"></feather-icon>
                Manage Plans
              </vs-button>
            </vs-col>
            <vs-col vs-w="3" vs-sm="6" vs-xs="12" class="mb-2">
              <vs-button color="success" class="w-full" to="/haioflash/nodes">
                <feather-icon icon="ServerIcon" class="mr-2"></feather-icon>
                Manage Nodes
              </vs-button>
            </vs-col>
            <vs-col vs-w="3" vs-sm="6" vs-xs="12" class="mb-2">
              <vs-button color="warning" class="w-full" to="/haioflash/regions">
                <feather-icon icon="MapPinIcon" class="mr-2"></feather-icon>
                Manage Regions
              </vs-button>
            </vs-col>
            <vs-col vs-w="3" vs-sm="6" vs-xs="12" class="mb-2">
              <vs-button color="danger" class="w-full" to="/haioflash/vms">
                <feather-icon icon="MonitorIcon" class="mr-2"></feather-icon>
                View All VMs
              </vs-button>
            </vs-col>
          </vs-row>
        </vx-card>
      </vs-col>
    </vs-row>
    
    <!-- Recent Activity -->
    <vs-row>
      <vs-col vs-w="12">
        <vx-card title="Recent VM Activity">
          <vs-table v-if="recentVms" :data="recentVms">
            <template slot="thead">
              <vs-th>VM ID</vs-th>
              <vs-th>Customer</vs-th>
              <vs-th>Plan</vs-th>
              <vs-th>Status</vs-th>
              <vs-th>Region</vs-th>
              <vs-th>Created</vs-th>
            </template>
            
            <template slot-scope="{ data }">
              <vs-tr :key="indextr" v-for="(tr, indextr) in data">
                <vs-td>{{ tr.microvm_id || 'N/A' }}</vs-td>
                <vs-td>
                  <div>
                    <strong>{{ tr.user_name || 'N/A' }}</strong><br>
                    <small class="text-grey">{{ tr.user_email }}</small>
                  </div>
                </vs-td>
                <vs-td>{{ tr.plan_name }}</vs-td>
                <vs-td>
                  <vs-chip :color="getStatusColor(tr.status)">
                    {{ tr.status }}
                  </vs-chip>
                </vs-td>
                <vs-td>{{ tr.region_name || 'N/A' }}</vs-td>
                <vs-td>{{ formatDate(tr.created_at) }}</vs-td>
              </vs-tr>
            </template>
          </vs-table>
        </vx-card>
      </vs-col>
    </vs-row>
  </div>
</template>

<script>
export default {
  name: 'HaioFlashDashboard',
  data() {
    return {
      stats: {
        total_vms: 0,
        running_vms: 0,
        total_customers: 0
      },
      revenue: {
        total_revenue: 0,
        monthly_revenue: 0,
        today_revenue: 0,
        total_vm_revenue: 0,
        total_ipv4_addon_revenue: 0,
        total_traffic_revenue: 0,
        vm_revenue: 0,
        vm_renewal_revenue: 0,
        ipv4_addon_revenue: 0,
        ipv4_addon_renewal_revenue: 0,
        traffic_package_revenue: 0
      },
      ipStats: {
        total_ips: 0,
        allocated_ips: 0,
        free_ips: 0,
        reserved_ips: 0,
        active_ips: 0,
        total_ipv4_addons: 0,
        active_ipv4_addons: 0,
        pending_ipv4_addons: 0,
        failed_ipv4_addons: 0
      },
      recentVms: [],
      loading: false
    }
  },
  mounted() {
    this.loadDashboardData()
  },
  methods: {
    async loadDashboardData() {
      this.loading = true
      try {
        const [statsRes, revenueRes, ipStatsRes, vmsRes] = await Promise.all([
          this.$http.get('/admin/haioflash/stats'),
          this.$http.get('/admin/haioflash/revenue'),
          this.$http.get('/admin/haioflash/ip-stats'),
          this.$http.get('/admin/haioflash/vms', { params: { per_page: 5 } })
        ])
        
        if (statsRes.data && statsRes.data.success) {
          this.stats = statsRes.data.data || {}
        }
        
        if (revenueRes.data && revenueRes.data.success) {
          this.revenue = revenueRes.data.data || {}
        }
        
        if (ipStatsRes.data && ipStatsRes.data.success) {
          this.ipStats = ipStatsRes.data.data || {}
        }
        
        if (vmsRes.data && vmsRes.data.success && vmsRes.data.data) {
          const paginatedData = vmsRes.data.data
          this.recentVms = Array.isArray(paginatedData.data) ? paginatedData.data.slice(0, 5) : []
        }
      } catch (error) {
        console.error('Dashboard load error:', error)
        const errorMessage = (error.response && error.response.data && error.response.data.message) || 'Failed to load dashboard data'
        this.$vs.notify({
          title: 'Error',
          text: errorMessage,
          color: 'danger'
        })
        // Keep arrays initialized to prevent spread errors
        this.recentVms = []
      } finally {
        this.loading = false
      }
    },
    getStatusColor(status) {
      const colors = {
        'running': 'success',
        'stopped': 'warning',
        'creating': 'primary',
        'error': 'danger'
      }
      return colors[status] || 'dark'
    },
    formatDate(date) {
      if (!date) return 'N/A'
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    formatCurrency(amount) {
      const formatted = Number(amount).toLocaleString('en-US')
      return `${formatted} Tomans`
    }
  }
}
</script>

<style scoped>
.text-center {
  text-align: center;
}
</style>
